import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import ButtonGroup from "@/components/ui/ButtonGroup";
import CountBox from "@/components/ui/CountBox";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import SelectBox from "@/components/ui/SelectBox";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import { ContextMenuItem } from "@/components/ui/ContextMenu";
import Table, { TableColumn } from "@/components/ui/Table";
import { Footer, FooterCell } from "@/components/layout/Footer";
import PictureOp from "@/components/modal/dispatch/sales/PictureOp";
import { useOrderSaleSlipReport, useOrderSlipReportOne } from "@/hooks/dispatch/dp_ordeer_slip/useOrderSlip";
import { OrderSaleSlipReportRow, SaleSlipReportSearchDto } from "@/interface/dispatch/dp_order_slip";
import PerformanceModal from "@/components/modal/performance/PerformanceModal";
import DriverSearchModal from "@/components/modal/performance/DriverSearchModal";
import { toast } from "sonner";
import { useReportOne, useUpdateSaleSlipLock, useDeleteSaleSlip, useUploadSaleSlipExcel, downloadSaleSlipUploadTemplate } from "@/hooks/performance/useSaleSlip";
import { ReportData } from "@/interface";
import Add from "@/components/modal/dispatch/order/Add";
import { useUserInfo } from "@/hooks/member/useMember";
import { DriverCode } from "@/interface/standard/st_driver_code";
import { api } from "@/lib/axios";
import { getLocalISOString, getDateOffset } from "@/lib/date";

export default function Sales() {
  const queryClient = useQueryClient();
  const [selectedValue, setSelectedValue] = useState("all");

  // 검색 필드 선택 및 검색어
  const [searchField, setSearchField] = useState("custName"); // 기본값: 거래처
  const [searchKeyword, setSearchKeyword] = useState(""); // 검색어 입력값
  const [searchField2, setSearchField2] = useState("carNumber"); // 두 번째 검색 필드 기본값: 차량번호
  const [searchKeyword2, setSearchKeyword2] = useState(""); // 두 번째 검색어 입력값

  // 정확히 일치 검색 여부
  const [exactMatch, setExactMatch] = useState(false);
  const [actualExactMatch, setActualExactMatch] = useState(false);

  // 실제 적용된 검색 조건 (조회 버튼 클릭 시 적용)
  const [appliedSearchDto, setAppliedSearchDto] = useState<SaleSlipReportSearchDto>({});

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [pictureOp, setPictureOp] = useState(false);
  const [dateBtnValue, setDateBtnValue] = useState("today");
  const [currentPage, setCurrentPage] = useState(0);
  const [allData, setAllData] = useState<OrderSaleSlipReportRow[]>([]);
  const [tableVer, setTableVer] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isPerformanceModalOpen, setIsPerformanceModalOpen] = useState(false);
  const [selectedSaleSlipCode, setSelectedSaleSlipCode] = useState<string | null>(null);
  const [performanceModalData, setPerformanceModalData] = useState<ReportData | null>(null);
  const [isCopyMode, setIsCopyMode] = useState(false); // 매출복사 모드인지 구분
  const [isViewMode, setIsViewMode] = useState(false); // 매출정보 보기 모드인지 구분
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedOrderSlipCode, setSelectedOrderSlipCode] = useState<string | null>(null);
  const [isDriverSearchModalOpen, setIsDriverSearchModalOpen] = useState(false);
  const [selectedDriverCode, setSelectedDriverCode] = useState<string | null>(null);
  const [selectedOrderSlipCodeForPhoto, setSelectedOrderSlipCodeForPhoto] = useState<string | null>(null);

  // saleSlipCode로 매출 전표 조회 (Report.tsx의 수정 버튼처럼 동작)
  const {
    data: reportData,
    isLoading: isLoadingReport,
    error: reportError,
  } = useReportOne(selectedSaleSlipCode || "", {
    enabled: isPerformanceModalOpen && !!selectedSaleSlipCode,
  });

  // orderSlipCode로 주문 전표 조회 (주문정보 클릭 시)
  const {
    data: orderSlipData,
    isLoading: isLoadingOrderSlip,
    error: orderSlipError,
  } = useOrderSlipReportOne(selectedOrderSlipCode || "", {
    enabled: isAddModalOpen && !!selectedOrderSlipCode,
  });

  // 마감 처리/해제 mutation
  const updateLockMutation = useUpdateSaleSlipLock();

  // 삭제 mutation
  const { mutate: deleteSaleSlip, isPending: isDeleting } = useDeleteSaleSlip();

  // 사용자 정보 가져오기 (삭제 시 delId로 사용)
  const { data: userInfo } = useUserInfo();

  // 엑셀 업로드 mutation
  const uploadExcelMutation = useUploadSaleSlipExcel();

  // 엑셀 업로드 파일 input ref
  const excelFileInputRef = useRef<HTMLInputElement>(null);

  // 테이블 새로고침 함수
  const refreshTable = useCallback(() => {
    // React Query 캐시 무효화하여 데이터 다시 fetch
    queryClient.invalidateQueries({ queryKey: ["orderSaleSlipReport"] });
    // 로컬 상태 초기화
    setAllData([]);
    setCurrentPage(0);
    setTableVer(v => v + 1);
  }, [queryClient]);

  // 에러 처리
  useEffect(() => {
    if (reportError && isPerformanceModalOpen) {
      toast.error("매출 전표를 불러오는데 실패했습니다.");
      setIsPerformanceModalOpen(false);
      setSelectedSaleSlipCode(null);
      setIsCopyMode(false);
    }
  }, [reportError, isPerformanceModalOpen]);

  // 주문 전표 에러 처리
  useEffect(() => {
    if (orderSlipError && isAddModalOpen) {
      toast.error("주문 전표를 불러오는데 실패했습니다.");
      setIsAddModalOpen(false);
      setSelectedOrderSlipCode(null);
    }
  }, [orderSlipError, isAddModalOpen]);

  // 매출복사 모드일 때 reportData의 매출 일자를 오늘 날짜로 변경
  useEffect(() => {
    if (isCopyMode && reportData && isPerformanceModalOpen) {
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const copiedData: ReportData = {
        ...reportData,
        contractDate: `${todayStr}T00:00:00`,
        transportDoneDate: `${todayStr}T00:00:00`,
        saleSlipCodeForTieBreak: "", // 매출복사는 새로운 데이터이므로 saleSlipCode 제거
      };

      setPerformanceModalData(copiedData);
    } else if (!isCopyMode && reportData && isPerformanceModalOpen) {
      // 매출정보 모드일 때는 reportData 그대로 사용
      setPerformanceModalData(null);
    }
  }, [isCopyMode, reportData, isPerformanceModalOpen]);

  // 익일 날짜를 기본값으로 설정
  const tomorrowStr = getDateOffset(1);

  const [startDate, setStartDate] = useState(tomorrowStr);
  const [endDate, setEndDate] = useState(tomorrowStr);

  const { data: orderSaleSlipReportData, isLoading } = useOrderSaleSlipReport({
    page: currentPage,
    size: 20,
    dateFrom: startDate,
    dateTo: endDate,
    searchDto: appliedSearchDto,
  });

  // Footer 총합 계산용 전체 데이터 조회 (size를 매우 크게 설정하여 모든 데이터 가져오기)
  const { data: totalData } = useOrderSaleSlipReport({
    page: 0,
    size: 999999, // 모든 데이터를 한 번에 가져오기
    dateFrom: startDate,
    dateTo: endDate,
    searchDto: appliedSearchDto,
  });

  // 조회 조건 변경 시 페이지네이션 상태 초기화
  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setLoadingMore(false);
    setTableVer(v => v + 1);
  }, [startDate, endDate, appliedSearchDto]);

  // 새 페이지 데이터 반영
  useEffect(() => {
    if (orderSaleSlipReportData && orderSaleSlipReportData.content) {
      if (currentPage === 0) {
        setAllData(orderSaleSlipReportData.content);
      } else if (orderSaleSlipReportData.content.length > 0) {
        setAllData(prev => [...prev, ...orderSaleSlipReportData.content!]);
      }
      setLoadingMore(false);
    }
  }, [orderSaleSlipReportData, currentPage]);

  const totalElements = orderSaleSlipReportData?.totalElements || 0;

  const hasMore = useMemo(() => {
    if (!orderSaleSlipReportData) return false;
    return !orderSaleSlipReportData.last && allData.length < totalElements;
  }, [orderSaleSlipReportData?.last, allData.length, totalElements]);

  // 검색 필드 옵션
  const searchFieldOptions = [
    { label: "거래처", value: "custName" },
    { label: "차량번호", value: "carNumber" },
    { label: "기사명", value: "driverName" },
    { label: "전화번호", value: "driverTel" },
    { label: "상차지", value: "inLocation" },
    { label: "하차지", value: "outLocation" },
    { label: "하차지역1", value: "outArea1" },
    { label: "하차지역2", value: "outArea2" },
    { label: "배차사원", value: "dispatchStaffName" },
    { label: "품목명", value: "itemName" },
  ];

  const handleReset = () => {
    setSearchField("custName");
    setSearchKeyword("");
    setSearchField2("carNumber");
    setSearchKeyword2("");
    setExactMatch(false);
    setActualExactMatch(false);
    setAppliedSearchDto({});
    // 익일 날짜로 초기화
    const resetDate = getDateOffset(1);
    setStartDate(resetDate);
    setEndDate(resetDate);
    setSelectedValue("all");
    setDateBtnValue("");
  };

  const handleSearch = () => {
    const newSearchDto: SaleSlipReportSearchDto = {
      exactMatch: exactMatch,
    };
    if (searchKeyword.trim()) {
      (newSearchDto as Record<string, string | boolean | undefined>)[searchField] = searchKeyword.trim();
    }
    if (searchKeyword2.trim()) {
      (newSearchDto as Record<string, string | boolean | undefined>)[searchField2] = searchKeyword2.trim();
    }
    setActualExactMatch(exactMatch);
    setAppliedSearchDto(newSearchDto);
    setAllData([]);
    setCurrentPage(0);
    setTableVer(v => v + 1);
  };

  // 무한 스크롤 설정
  const infiniteScrollPage = useInfiniteScrollPage({
    data: allData,
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore: hasMore,
    onDataLoad: async () => {
      if (hasMore && !loadingMore) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
  });

  const columns: TableColumn[] = [
    {
      key: "baseDate",
      header: "매출일자\n(주문일자)",
      sticky: "left",
      render: (value: string, row: OrderSaleSlipReportRow) => {
        // 날짜 포맷팅 헬퍼 함수
        const formatDate = (date: string | undefined): string => {
          if (!date) return "";
          return String(date).split("T")[0];
        };

        // 우선순위: saleDate > orderDate > baseDate > value
        if (row?.saleDate) return formatDate(row.saleDate);
        if (row?.orderDate) return `(${formatDate(row.orderDate)})`;
        if (row?.baseDate) return formatDate(row.baseDate);
        if (value) return formatDate(value);
        return "";
      },
    },
    { key: "custName", header: "매출처", bodyAlign: "left", sticky: "left" },
    { key: "itemName", header: "품목명", bodyAlign: "left", sticky: "left" },

    { key: "carNumber", header: "차량번호", bodyAlign: "left", group: "차량정보" },
    { key: "driverName", header: "기사명", group: "차량정보" },
    { key: "driverTel", header: "전화번호", group: "차량정보" },

    { key: "inBranch1", header: "대리점1", bodyAlign: "left", group: "상차정보" },
    { key: "inLocation1", header: "상차지1", bodyAlign: "left", group: "상차정보" },
    {
      key: "inQty1",
      header: "수량1",
      bodyAlign: "right",
      group: "상차정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },

    { key: "outLocation1", header: "하차지1", bodyAlign: "left", group: "하차정보" },
    { key: "outArea1", header: "지역1", bodyAlign: "left", group: "하차정보" },
    {
      key: "outQty1",
      header: "수량1",
      bodyAlign: "right",
      group: "하차정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    { key: "outLocation2", header: "하차지2", bodyAlign: "left", group: "하차정보" },
    { key: "outArea2", header: "지역2", bodyAlign: "left", group: "하차정보" },
    {
      key: "outQty2",
      header: "수량2",
      bodyAlign: "right",
      group: "하차정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    { key: "outLocation3", header: "하차지3", bodyAlign: "left", group: "하차정보" },
    { key: "outArea3", header: "지역3", bodyAlign: "left", group: "하차정보" },
    {
      key: "outQty3",
      header: "수량3",
      bodyAlign: "right",
      group: "하차정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },

    {
      key: "saleUnitPrice",
      header: "단가",
      bodyAlign: "right",
      group: "매출정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "saleAmount",
      header: "매출액",
      bodyAlign: "right",
      group: "매출정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "saleTotal",
      header: "합계",
      bodyAlign: "right",
      group: "매출정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },

    {
      key: "buyUnitPrice",
      header: "단가",
      bodyAlign: "right",
      group: "매입정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "buyAmount",
      header: "매입액",
      bodyAlign: "right",
      group: "매입정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "buyTotal",
      header: "합계",
      bodyAlign: "right",
      group: "매입정보",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },

    {
      key: "profitAmt",
      header: "금액",
      bodyAlign: "right",
      group: "운송마진",
      render: (value: number) => (value ? value.toLocaleString() : ""),
    },
    {
      key: "profitRate",
      header: "비율",
      bodyAlign: "right",
      group: "운송마진",
      render: (value: number) => (value ? `${value.toFixed(2)}%` : ""),
    },

    { key: "remark", header: "비고", bodyAlign: "left" },
  ];

  const getContextMenuItems = (row: any, index: number): ContextMenuItem[] => {
    // 파란색 텍스트 조건: saleSlipCode가 없거나 빈 문자열
    const hasNoSaleSlipCode = !row.saleSlipCode || row.saleSlipCode.trim() === "";
    // 일반 검색 텍스트 조건: saleSlipCode가 있음
    const hasSaleSlipCode = !hasNoSaleSlipCode;

    return [
      {
        label: "주문정보",
        onClick: () => {
          // orderSlipCode로 주문 전표 조회 후 Add 모달을 edit 모드로 열기
          if (!row.orderSlipCode) {
            toast.error("주문 전표 코드가 없습니다.");
            return;
          }
          setSelectedOrderSlipCode(row.orderSlipCode);
          setIsAddModalOpen(true);
        },
      },
      { divider: true },
      {
        label: "매출처리",
        disabled: hasSaleSlipCode, // saleSlipCode가 있으면 disabled (일반 검색 텍스트)
        onClick: async () => {
          // OrderSaleSlipReportRow를 ReportData로 변환 (PerformanceModal에서 사용 가능한 모든 필드 매핑)
          console.log(row);

          // driver_order에서 실제 상차수량/하차수량 조회
          let actualInQty1 = row.inQty1 || 0;
          let actualOutQty1 = row.outQty1 || 0;
          let actualOutQty2 = row.outQty2 || 0;
          let actualOutQty3 = row.outQty3 || 0;

          if (row.orderSlipCode) {
            try {
              const driverOrderRes = await api.get(`/driver-order/detail/${row.orderSlipCode}`);
              const driverOrder = driverOrderRes.data || driverOrderRes;
              if (driverOrder) {
                // 실제 상차수량이 있으면 사용, 없으면 기존 값 유지
                actualInQty1 = driverOrder.qty2 ?? driverOrder.qty1 ?? row.inQty1 ?? 0;
                // 실제 하차수량이 있으면 사용, 없으면 기존 값 유지
                actualOutQty1 = driverOrder.outQty1R ?? driverOrder.outQty1 ?? row.outQty1 ?? 0;
                actualOutQty2 = driverOrder.outQty2R ?? driverOrder.outQty2 ?? row.outQty2 ?? 0;
                actualOutQty3 = driverOrder.outQty3R ?? driverOrder.outQty3 ?? row.outQty3 ?? 0;
                console.log("[매출처리] driver_order 실제 상차수량:", { actualInQty1 });
                console.log("[매출처리] driver_order 실제 하차수량:", { actualOutQty1, actualOutQty2, actualOutQty3 });
              }
            } catch (error: any) {
              // 401 에러 등 인증 문제 시에도 기존 값으로 계속 진행
              console.log("[매출처리] driver_order 조회 실패, 기존 값 사용:", error?.message || error);
            }
          }

          // 매출 계산: 주문일보 단가 → 매출액으로 직접 사용
          // 주문일보에서 등록한 단가는 매출정보의 "매출액" InputBox에 들어감
          const saleAmount = row.saleUnitPrice || 0;
          const saleVat = Math.round(saleAmount * 0.1);
          const saleTotal = saleAmount + saleVat;

          // 운송마진 비율 (주문일보의 매입액 %)
          const profitRateFromOrder = row.profitRate || 0;

          const reportData: ReportData = {
            // 기본 정보
            reportType: "1", // 계약+운송 (기본값)
            contractUniqueNo: row.orderSlipCode || "",
            saleSlipCompanyCode: row.companyCode || "",

            // 운송의뢰자
            custName: row.custName || "",
            custBizNumber: "", // OrderSaleSlipReportRow에는 없음

            // 계약정보
            contractDate: row.orderDate ? `${row.orderDate}T00:00:00` : getLocalISOString(),
            contractAmount: saleTotal, // 계산된 합계
            itemGroupCode: "", // OrderSaleSlipReportRow에는 없음
            otherTransportUse: "", // OrderSaleSlipReportRow에는 없음

            // 상차지/하차지 정보 (PerformanceModal에서 사용)
            pickupBranch: row.inBranch1 || "",
            pickupLocation: row.inLocation1 || "",
            pickupQty: actualInQty1,
            dropoffLocation: row.outLocation1 || "",
            dropoffArea1: row.outArea1 || "",
            dropoffArea2: row.outArea2 || "",
            dropoffArea3: row.outArea3 || null,
            // 실제 하차수량 사용 (driver_order에서 조회한 값)
            dropoffQty1: actualOutQty1,
            dropoffQty2: actualOutQty2,
            dropoffQty3: actualOutQty3,

            // 배차정보 (PerformanceModal에서 사용)
            vehicleNumber: row.carNumber || "",
            transportDoneDate: row.orderDate ? `${row.orderDate}T00:00:00` : getLocalISOString(),
            transportFee: 0, // 기본값

            // 매출정보 (주문일보 단가 → 매출액으로 직접 사용)
            saleUnitPrice: 0, // 단가는 사용하지 않음
            saleAmount: saleAmount, // 주문일보 단가가 매출액으로 들어감
            saleMoveFee: 0, // 직배비는 기본 0
            saleVat: saleVat,
            saleTotal: saleTotal,

            // 매입정보 (운송마진 비율로 자동 계산됨)
            buyUnitPrice: 0,
            buyAmount: 0,
            buyMoveFee: 0,
            buyLaborFee: 0,
            buyMultiFee: 0,
            buyWeightFee: 0,
            buyVat: 0,
            buyTotal: 0,

            // 운송마진 (주문일보의 매입액 % → 비율로 전달)
            transportProfitAmt: 0, // 자동 계산됨
            transportProfitRate: profitRateFromOrder, // 주문일보 매입액 %가 운송마진 비율로 들어감

            // 위탁 계약정보 (기본값)
            consignBizNumber: row.consignBizNumber || "",
            consignContractDate: row.consignContractDate
              ? `${row.consignContractDate}T00:00:00`
              : getLocalISOString(),
            consignContractAmt: row.buyTotal || 0,
            freightNetUse: row.freightNetUse || "1", // OrderSaleSlipReportRow에는 없음

            // 기타 (PerformanceModal에서 사용하는 필드들)
            itemName: row.itemName || "",
            driverName: row.driverName || "",
            driverTel: row.driverTel || "",
            saleSlipShapeCode: row.shapeCode || "", // OrderSaleSlipReportRow에는 없음
            saleSlipUnitCode: row.unitCode || "", // OrderSaleSlipReportRow에는 없음
            saleSlipCompanyGb: row.companyGb || "", // 기본값: 계약+운송
            saleSlipCustCode: row.custCode || "", // OrderSaleSlipReportRow에는 없음
            saleSlipItemCode: row.itemCode || "", // OrderSaleSlipReportRow에는 없음
            saleSlipCarCode: row.carCode || "", // OrderSaleSlipReportRow에는 없음
            saleSlipDriverCode: row.driverCode || "", // OrderSaleSlipReportRow에는 없음
            saleSlipOrderSlipCode: row.orderSlipCode || "", // 주문 전표 코드
            saleSlipRemark: row.remark || "",
            saleSlipReturnGb: false, // 기본값: Y
          };

          setPerformanceModalData(reportData);
          setIsViewMode(false); // 추가 모드
          setIsPerformanceModalOpen(true);
        },
      },
      {
        label: "매출정보",
        disabled: hasNoSaleSlipCode, // saleSlipCode가 없으면 disabled (파란색 텍스트 조건)
        onClick: () => {
          // saleSlipCode로 매출 전표 조회 (수정 모드)
          if (!row.saleSlipCode) {
            toast.error("매출 전표 코드가 없습니다.");
            return;
          }
          setIsCopyMode(false);
          setIsViewMode(false); // 수정 모드
          setSelectedSaleSlipCode(row.saleSlipCode);
          setIsPerformanceModalOpen(true);
        },
      },
      {
        label: "매출복사",
        disabled: hasNoSaleSlipCode, // saleSlipCode가 없으면 disabled (매출 처리가 안된 경우)
        onClick: () => {
          // saleSlipCode로 매출 전표 조회 후 매출 일자만 오늘 날짜로 변경
          if (!row.saleSlipCode) {
            toast.error("매출 전표 코드가 없습니다.");
            return;
          }
          setIsCopyMode(true);
          setIsViewMode(false); // 추가 모드
          setSelectedSaleSlipCode(row.saleSlipCode);
          setIsPerformanceModalOpen(true);
        },
      },
      { divider: true },
      {
        label: "기사정보",
        onClick: () => {
          // 기사 코드가 있으면 해당 기사로 필터링하여 모달 열기
          const searchValue = row.driverName || "";
          setSelectedDriverCode(searchValue);
          setIsDriverSearchModalOpen(true);
        },
      },
      {
        label: "운행사진",
        onClick: () => {
          // orderSlipCode로 운행 사진 모달 열기
          if (!row.orderSlipCode) {
            toast.error("주문 전표 코드가 없습니다.");
            return;
          }
          setSelectedOrderSlipCodeForPhoto(row.orderSlipCode);
          setPictureOp(true);
        },
      },
    ];
  };

  // 마감 처리/해제 함수
  const handleUpdateLock = (lock: boolean) => {
    if (selectedRows.length === 0) {
      toast.warning("선택된 항목이 없습니다.");
      return;
    }

    // 선택된 행들의 saleSlipCode 가져오기
    const saleSlipCodes = selectedRows
      .map(index => allData[index]?.saleSlipCode)
      .filter((code): code is string => !!code && code.trim() !== "");

    if (saleSlipCodes.length === 0) {
      toast.error(`마감 ${lock ? "처리" : "해제"}할 수 있는 항목이 없습니다. (saleSlipCode가 없음)`);
      return;
    }

    // 각 saleSlipCode에 대해 마감 처리/해제
    saleSlipCodes.forEach(saleSlipCode => {
      updateLockMutation.mutate({ saleSlipCode, lock });
    });
  };

  // 삭제 함수
  const handleDelete = () => {
    if (selectedRows.length === 0) {
      toast.warning("선택된 항목이 없습니다.");
      return;
    }

    // 선택된 행들의 saleSlipCode와 orderSlipCode 가져오기
    const selectedItems = selectedRows.map(index => allData[index]).filter(item => item);

    const saleSlipCodes = selectedItems
      .map(item => item?.saleSlipCode)
      .filter((code): code is string => !!code && code.trim() !== "");

    const orderSlipCodes = selectedItems
      .map(item => item?.orderSlipCode)
      .filter((code): code is string => !!code && code.trim() !== "");

    if (saleSlipCodes.length === 0 && orderSlipCodes.length === 0) {
      toast.warning("삭제할 수 있는 항목이 없습니다. (saleSlipCode 또는 orderSlipCode가 없음)");
      return;
    }

    // 삭제 확인
    const totalCount = saleSlipCodes.length + orderSlipCodes.length;
    if (!confirm(`${totalCount}개의 항목을 삭제하시겠습니까?`)) {
      return;
    }

    const delId = userInfo?.name || "";
    if (!delId) {
      toast.error("사용자 정보를 가져올 수 없습니다.");
      return;
    }

    // 삭제 실행
    deleteSaleSlip(
      { saleSlipCodes, orderSlipCodes, delId },
      {
        onSuccess: () => {
          setSelectedRows([]);
          // 테이블 데이터 새로고침을 위해 페이지 초기화
          setAllData([]);
          setCurrentPage(0);
          setTableVer(v => v + 1);
        },
      }
    );
  };

  // 엑셀 업로드 핸들러
  const handleExcelUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const writeId = userInfo?.name || "";
    if (!writeId) {
      toast.error("사용자 정보를 가져올 수 없습니다.");
      return;
    }

    try {
      const result = await uploadExcelMutation.mutateAsync({ file, writeId });

      if (result.success) {
        toast.success(result.message || "업로드 완료");
        refreshTable();
      } else {
        toast.error(result.message || "업로드 실패");

        // 오류 파일 다운로드 (Base64)
        if (result.errorFileBase64 && result.errorFileName) {
          const byteCharacters = atob(result.errorFileBase64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = result.errorFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "업로드 중 오류가 발생했습니다.");
    }

    // 파일 input 초기화 (같은 파일 재선택 가능하도록)
    if (excelFileInputRef.current) {
      excelFileInputRef.current.value = "";
    }
  };

  // Footer 그리드 데이터 계산 (전체 데이터 기준)
  const footerGrid: (FooterCell | null)[][] = useMemo(() => {
    // 전체 데이터가 없으면 빈 값 반환
    if (!totalData?.content || totalData.content.length === 0) {
      return [
        [
          { label: "상차수량", value: "0.00" },
          { label: "매출", isLabelOnly: true, rowSpan: 3 },
          { label: "매출액", value: "0" },
          { label: "매입", isLabelOnly: true, rowSpan: 3 },
          { label: "매입액", value: "0" },
        ],
        [
          { label: "하차수량", value: "0.00" },
          null,
          { label: "직배비", value: "0" },
          null,
          { label: "직배비", value: "0" },
        ],
        [
          { label: "운송마진", value: "0" },
          null,
          { label: "합계액", value: "0" },
          null,
          { label: "합계액", value: "0" },
        ],
      ];
    }

    // 전체 데이터 기준으로 합산
    // 상차수량: inQty1만 (OrderSlipReportProjection에는 inQty1만 있음)
    const totalInQty = totalData.content.reduce((sum, item) => {
      const inQty1 = item.inQty1 || 0;
      return sum + inQty1;
    }, 0);

    // 하차수량: 각 row의 outQty1 + outQty2 + outQty3를 모두 합산
    const totalOutQty = totalData.content.reduce((sum, item) => {
      // null, undefined, 빈 문자열 등을 0으로 처리
      const outQty1 = item.outQty1 != null ? Number(item.outQty1) : 0;
      const outQty2 = item.outQty2 != null ? Number(item.outQty2) : 0;
      const outQty3 = item.outQty3 != null ? Number(item.outQty3) : 0;
      return sum + (outQty1 || 0) + (outQty2 || 0) + (outQty3 || 0);
    }, 0);

    // 매출액: saleAmount (공급가)
    const totalSaleAmount = totalData.content.reduce((sum, item) => sum + (item.saleAmount || 0), 0);
    const totalSaleTotal = totalData.content.reduce((sum, item) => sum + (item.saleTotal || 0), 0);
    // 매출 직배비 = 합계 - 매출액 (saleTotal = saleAmount + 직배비)
    const totalSaleMoveAmt = totalSaleTotal - totalSaleAmount;

    // 매입액: buyAmount (공급가)
    const totalBuyAmount = totalData.content.reduce((sum, item) => sum + (item.buyAmount || 0), 0);
    const totalBuyTotal = totalData.content.reduce((sum, item) => sum + (item.buyTotal || 0), 0);
    // 매입 직배비 = 합계 - 매입액 (buyTotal = buyAmount + 직배비)
    const totalBuyMoveAmt = totalBuyTotal - totalBuyAmount;

    // 운송마진: profitAmt
    const totalProfitAmt = totalData.content.reduce((sum, item) => sum + (item.profitAmt || 0), 0);

    return [
      // 1행
      [
        { label: "상차수량", value: totalInQty.toFixed(2) },
        { label: "매출", isLabelOnly: true, rowSpan: 3 },
        { label: "매출액", value: totalSaleAmount.toLocaleString() },
        { label: "매입", isLabelOnly: true, rowSpan: 3 },
        { label: "매입액", value: totalBuyAmount.toLocaleString() },
      ],
      // 2행
      [
        { label: "하차수량", value: totalOutQty.toFixed(2) },
        null,
        { label: "직배비", value: totalSaleMoveAmt.toLocaleString() },
        null,
        { label: "직배비", value: totalBuyMoveAmt.toLocaleString() },
      ],
      // 3행
      [
        { label: "운송마진", value: totalProfitAmt.toLocaleString() },
        null,
        { label: "합계액", value: totalSaleTotal.toLocaleString() },
        null,
        { label: "합계액", value: totalBuyTotal.toLocaleString() },
      ],
    ];
  }, [totalData]);

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex items-end mb-3">
          <SelectBox
            label={searchFieldOptions.find(opt => opt.value === searchField)?.label || "검색필드"}
            className="min-w-24"
            value={searchField}
            onChange={e => setSearchField(e)}
            options={searchFieldOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-50"
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="mr-3" />
          <SelectBox
            label={searchFieldOptions.find(opt => opt.value === searchField2)?.label || "검색필드"}
            className="min-w-24"
            value={searchField2}
            onChange={e => setSearchField2(e)}
            options={searchFieldOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-50"
            value={searchKeyword2}
            onChange={e => setSearchKeyword2(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="mr-3" />
          <DateBox
            label="매출일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
            }}
          />
        </div>
        <div className="flex items-end">
          <ButtonGroup
            label="표시선택"
            options={[
              { value: "all", label: "전체" },
              { value: "option1", label: "마감" },
              { value: "option2", label: "진행중" },
            ]}
            value={selectedValue}
            onChange={setSelectedValue}
          />
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={totalElements} />
          <div className="flex gap-2">
            <PermissionButton permission="canUpdate" variant="deepGreen" onClick={() => handleUpdateLock(true)}>
              {updateLockMutation.isPending ? "처리 중..." : "마감 처리"}
            </PermissionButton>
            <PermissionButton permission="canUpdate" variant="deepGreen" onClick={() => handleUpdateLock(false)}>
              {updateLockMutation.isPending ? "처리 중..." : "마감 해제"}
            </PermissionButton>
            <PermissionButton
              permission="canCreate"
              variant="primary"
              onClick={() => excelFileInputRef.current?.click()}
              disabled={uploadExcelMutation.isPending}
            >
              {uploadExcelMutation.isPending ? "업로드 중..." : "엑셀업로드"}
            </PermissionButton>
            <input
              type="file"
              ref={excelFileInputRef}
              onChange={handleExcelUpload}
              accept=".xlsx,.xls"
              style={{ display: "none" }}
            />
            <PermissionButton permission="canCreate" variant="etc" onClick={downloadSaleSlipUploadTemplate}>
              양식
            </PermissionButton>
            <PermissionButton
              permission="canCreate"
              variant="etc"
              onClick={() => {
                setIsPerformanceModalOpen(true);
                setSelectedSaleSlipCode(null);
                setPerformanceModalData(null);
                setIsCopyMode(false);
                setIsViewMode(false);
              }}
            >
              추가
            </PermissionButton>
            <PermissionButton
              permission="canDelete"
              variant="etc"
              onClick={handleDelete}
              disabled={isDeleting || selectedRows.length === 0}
            >
              {isDeleting ? "삭제 중..." : "삭제"}
            </PermissionButton>
            <PrintButton data={allData} columns={columns} title="매출관리" />
            <ExcelButton data={allData} columns={columns} fileName="매출관리" />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`sales-table-v${tableVer}`}
            resizable
            {...infiniteScrollPage.tableProps}
            className="h-full"
            columns={columns}
            data={allData}
            showCheckbox={true}
            selectedRows={selectedRows}
            stickyCheckbox
            onRowSelect={setSelectedRows}
            onRowDoubleClick={(row) => {
              if (row.saleSlipCode && row.saleSlipCode.trim() !== "") {
                // 매출 처리된 행: 매출정보 수정
                setIsCopyMode(false);
                setIsViewMode(false);
                setSelectedSaleSlipCode(row.saleSlipCode);
                setIsPerformanceModalOpen(true);
              } else if (row.orderSlipCode) {
                // 매출 미처리 행: 주문정보 보기/수정
                setSelectedOrderSlipCode(row.orderSlipCode);
                setIsAddModalOpen(true);
              }
            }}
            contextMenuItems={getContextMenuItems}
            rowTextColor={(row, index) => {
              // lock이 true이면 회색 (#AAAAAA)
              if (row.lock === true || row.lock === "Y" || row.lock === "true") {
                return "#AAAAAA";
              }
              // saleSlipCode가 없으면 파란색 (#1F4AB8)
              if (!row.saleSlipCode || row.saleSlipCode.trim() === "") {
                return "#1F4AB8";
              }
              return null;
            }}
            getRowBgClassName={(row, index) => {
              // lock이 true이면 배경색 #F7F7F7
              if (row.lock === true || row.lock === "Y" || row.lock === "true") {
                return "bg-[#F7F7F7]";
              }
              return null;
            }}
            maxHeight="100%"
            loadingMore={loadingMore}
            loading={isLoading && currentPage === 0}
            emptyMessage="데이터가 없습니다."
          />
        </div>
      </main>

      <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer>

      <PictureOp
        isOpen={pictureOp}
        onClose={() => {
          setPictureOp(false);
          setSelectedOrderSlipCodeForPhoto(null);
        }}
        orderSlipCode={selectedOrderSlipCodeForPhoto}
      />

      <PerformanceModal
        isOpen={isPerformanceModalOpen}
        onClose={() => {
          setIsPerformanceModalOpen(false);
          setSelectedSaleSlipCode(null);
          setPerformanceModalData(null);
          setIsCopyMode(false);
          setIsViewMode(false);
        }}
        onSave={() => {
          // 저장 성공 시 테이블 새로고침
          refreshTable();
        }}
        mode={
          isViewMode
            ? "view" // 매출정보는 보기 전용 모드
            : isCopyMode
              ? "add" // 매출복사는 새로운 데이터이므로 add 모드
              : reportData?.saleSlipCodeForTieBreak || performanceModalData?.saleSlipCodeForTieBreak
                ? "edit"
                : "add"
        }
        initialData={isCopyMode ? performanceModalData || undefined : reportData || performanceModalData || undefined}
      />

      <Add
        isOpen={isAddModalOpen}
        mode="edit"
        initialData={orderSlipData || undefined}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedOrderSlipCode(null);
        }}
        onSave={data => {
          setIsAddModalOpen(false);
          setSelectedOrderSlipCode(null);
          // 저장 성공 시 테이블 새로고침
          refreshTable();
        }}
      />

      <DriverSearchModal
        isOpen={isDriverSearchModalOpen}
        onClose={() => {
          setIsDriverSearchModalOpen(false);
          setSelectedDriverCode(null);
        }}
        onSelect={(driver: DriverCode) => {
          setIsDriverSearchModalOpen(false);
          setSelectedDriverCode(null);
        }}
        initialSearchValue={selectedDriverCode || undefined}
      />
    </div>
  );
}
